import { useState } from "react";
import { Link, useSearchParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { MailCheck } from "lucide-react";
import { AuthError, confirmEmail } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Landing page for the verification link. Confirmation happens on an explicit
 * button click, not on mount, so link prefetchers can't burn the token.
 */
export function VerifyEmailPage() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get("token");
  const queryClient = useQueryClient();
  const [state, setState] = useState<"idle" | "busy" | "done" | "invalid">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onConfirm() {
    setState("busy");
    setError(null);
    try {
      await confirmEmail(token!);
      // The profile's verification line reads from the "me" cache.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      setState("done");
    } catch (err) {
      if (err instanceof AuthError && err.status === 401) setState("invalid");
      else {
        setState("idle");
        setError(err instanceof Error ? err.message : t("verify.failed"));
      }
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("verify.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!token || state === "invalid" ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive">{t("verify.invalid")}</p>
              <p className="text-sm text-muted-foreground">
                {t("verify.requestNewPre")}{" "}
                <Link
                  href="/profile"
                  className="font-medium text-primary transition-colors hover:underline"
                >
                  {t("verify.yourProfile")}
                </Link>
                .
              </p>
            </div>
          ) : state === "done" ? (
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm text-primary">
                <MailCheck aria-hidden className="size-4 shrink-0" />
                {t("verify.done")}
              </p>
              <Button asChild>
                <Link href="/profile">{t("verify.goProfile")}</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("verify.intro")}</p>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="button" onClick={onConfirm} disabled={state === "busy"} className="w-full">
                {state === "busy" ? t("verify.busy") : t("verify.submit")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
